import { TooltipComponent } from "../../components/Component";
import React from "react";
import { useFormContext } from "react-hook-form";
import { FieldErrors, FieldValues } from "react-hook-form";

interface FormInputProps {
  name: string;
  label: any;
  optionLabel?: string;
  optionLabelWidth?: any;
  unMaskView?: boolean;
  type?: string;
  required?: boolean;
  hideRequiredMark?: boolean;
  minLength?: number;
  maxLength?: number;
  placeholder?: string;
  column?: string;
  pattern?: RegExp;
  patternErrorMessage?: string;
  customValidation?: { validate: (value: string) => boolean | string };
  defaultValue?: string | number;
  disabled?: boolean;
  tooltipText?: string;
  min?: any;
  onKeyDown?: any;
  onInput?: (event: React.FormEvent<HTMLInputElement>) => void;
  accept?: string;
}

const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  type = "text",
  optionLabel,
  optionLabelWidth = "34px",
  unMaskView = false,
  required = false,
  hideRequiredMark = false,
  minLength,
  maxLength,
  placeholder,
  column,
  pattern,
  patternErrorMessage,
  customValidation,
  defaultValue,
  disabled,
  tooltipText,
  min,
  onKeyDown,
  onInput,
  accept = ".doc, .docx, .png, .pdf, .jpeg, .jpg, .gif",
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const errorMessage = getErrorMessage(errors, name);

  const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
    if (onInput) {
      onInput(event);
    }
  };

  return (
    <div className={column}>
      <div
        className={`form-group ${optionLabel ? "mb-1" : ""} ${
          unMaskView ? "unmask-view" : ""
        }`}
      >
        {label && (
          <div className="form-label-wrapper">
            <label className="form-label" htmlFor={name}>
              {label}
              {required && !hideRequiredMark && (
                <span className="mandatory">*</span>
              )}
            </label>
            {tooltipText && name && (
              <TooltipComponent
                iconClass="card-hint"
                icon="help-fill"
                direction="top"
                id={`${name}-tooltip`}
                text={tooltipText}
                containerClassName={""}
              />
            )}
          </div>
        )}
        <div className="form-control-wrap">
          <div
            className={`form-control-select ${
              optionLabel ? "d-flex align-items-center" : ""
            }`}
          >
            {optionLabel && (
              <label
                className={`form-label`}
                style={{ minWidth: optionLabelWidth }}
                htmlFor={name}
              >
                {optionLabel}
                {required && !hideRequiredMark && (
                  <span className="mandatory">*</span>
                )}
              </label>
            )}
            <input
              type={type}
              id={name}
              placeholder={placeholder}
              className="form-control"
              {...register(name, {
                required: required ? "This field is required" : undefined,
                minLength: minLength
                  ? {
                      value: minLength,
                      message: `Must be at least ${minLength} characters`,
                    }
                  : undefined,
                maxLength: maxLength
                  ? {
                      value: maxLength,
                      message: `Cannot be more than ${maxLength} characters`,
                    }
                  : undefined,
                pattern: pattern
                  ? {
                      value: pattern,
                      message: patternErrorMessage || "Invalid format",
                    }
                  : undefined,
                validate: customValidation
                  ? customValidation.validate
                  : undefined,
              })}
              onInput={handleInput}
              // onInput={onInput}
              defaultValue={defaultValue}
              min={min}
              step={type === "number" ? "any" : undefined}
              // onKeyDown={handleKeyDown}
              onKeyDown={onKeyDown}
              autoComplete="off"
              disabled={disabled}
              accept={type === "file" ? accept : undefined}
            />
          </div>
          {errorMessage && !optionLabel && (
            <p className="mandatory">{errorMessage}</p>
          )}
        </div>
        {errorMessage && optionLabel && (
          <p className="mandatory">{errorMessage}</p>
        )}
      </div>
    </div>
  );
};

function getErrorMessage<T extends FieldValues>(
  errors: FieldErrors<T>,
  fieldName: string
): string | undefined {
  const parts = fieldName.split(/[\[\].]+/).filter(Boolean);
  let currentError: any = errors;

  for (const part of parts) {
    if (currentError && typeof currentError === "object") {
      currentError = currentError[part];
    } else {
      return undefined;
    }
  }

  if (
    currentError &&
    "message" in currentError &&
    typeof currentError.message === "string"
  ) {
    return currentError.message;
  }

  return undefined;
}

export default FormInput;
